import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API, timeout: 30000 });

// Retry interceptor — retry failed requests up to 3 times with 2s delay
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    if (!config || config._retryCount >= 3) return Promise.reject(error);
    // Only retry on network errors or 5xx server errors, not 4xx client errors
    const status = error.response?.status;
    if (status && status < 500) return Promise.reject(error);
    config._retryCount = (config._retryCount || 0) + 1;
    await new Promise(r => setTimeout(r, 2000));
    return api(config);
  }
);

export const getConfig = () => api.get("/config").then(r => r.data);
export const exportUrl = (type) => `${API}/export/${type}`;
export const listQuotes = (search = "") => api.get("/quotes", { params: { search } }).then(r => r.data);
export const getQuote = (id) => api.get(`/quotes/${id}`).then(r => r.data);
export const createQuote = (data) => api.post("/quotes", data).then(r => r.data);
export const updateQuote = (id, data) => {
  const userName = localStorage.getItem("desktop_user_name") || localStorage.getItem("team_member_name") || "";
  return api.put(`/quotes/${id}?changed_by=${encodeURIComponent(userName)}`, data).then(r => r.data);
};
export const deleteQuote = (id) => api.delete(`/quotes/${id}`).then(r => r.data);

export const addItem = (quoteId, data) => api.post(`/quotes/${quoteId}/items`, data).then(r => r.data);
export const updateItem = (quoteId, itemId, data) => api.put(`/quotes/${quoteId}/items/${itemId}`, data).then(r => r.data);
export const deleteItem = (quoteId, itemId) => api.delete(`/quotes/${quoteId}/items/${itemId}`).then(r => r.data);

export const getMediaUrl = (path) => `${API}/media/${encodeURIComponent(path)}`;
export const uploadMedia = async (quoteId, file, category) => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("category", category);
  return api.post(`/quotes/${quoteId}/media`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 }).then(r => r.data);
};
export const deleteMedia = (quoteId, mediaId) => api.delete(`/quotes/${quoteId}/media/${mediaId}`).then(r => r.data);

export const getQuotePdfUrl = (id) => `${API}/quotes/${id}/pdf`;
export const getInvoicePdfUrl = (id) => `${API}/quotes/${id}/invoice-pdf`;

export const sendQuoteEmail = (id, data) => api.post(`/quotes/${id}/send`, data).then(r => r.data);
export const sendInvoiceEmail = (id, data) => api.post(`/quotes/${id}/send-invoice`, data).then(r => r.data);

export const listClients = (search = "") => api.get("/clients", { params: { search } }).then(r => r.data);
export const createClient = (data) => api.post("/clients", data).then(r => r.data);
export const deleteClient = (id) => api.delete(`/clients/${id}`).then(r => r.data);

export const listTeamMembers = () => api.get("/team-members").then(r => r.data);
export const createTeamMember = (data) => api.post("/team-members", data).then(r => r.data);
export const deleteTeamMember = (id) => api.delete(`/team-members/${id}`).then(r => r.data);

export const bookJob = (id, data) => api.post(`/quotes/${id}/book`, data).then(r => r.data);

export const listAbsences = () => api.get("/absences").then(r => r.data);
export const createAbsence = (data) => api.post("/absences", data).then(r => r.data);
export const deleteAbsence = (id) => api.delete(`/absences/${id}`).then(r => r.data);

export const listAppointments = () => api.get("/appointments").then(r => r.data);
export const createAppointment = (data) => api.post("/appointments", data).then(r => r.data);
export const deleteAppointment = (id) => api.delete(`/appointments/${id}`).then(r => r.data);

export const getPricingSettings = () => api.get("/settings/pricing").then(r => r.data);
export const updatePricingSettings = (data) => api.put("/settings/pricing", data).then(r => r.data);

export const getJobProfit = (quoteId) => api.get(`/quotes/${quoteId}/profit`).then(r => r.data);
export const saveJobProfit = (quoteId, data, user = "") => api.put(`/quotes/${quoteId}/profit?changed_by=${encodeURIComponent(user)}`, data).then(r => r.data);
export const finaliseJobProfit = (quoteId, summary, user = "") => api.post(`/quotes/${quoteId}/profit/finalise?changed_by=${encodeURIComponent(user)}`, { summary }).then(r => r.data);
export const unlockJobProfit = (quoteId, user = "") => api.post(`/quotes/${quoteId}/profit/unlock?changed_by=${encodeURIComponent(user)}`).then(r => r.data);
export const listJobProfitability = () => api.get("/job-profitability").then(r => r.data);

export const getMaterialsPdfUrl = (id) => `${API}/quotes/${id}/materials-pdf`;

export const addSupplier = (name) => api.post("/settings/suppliers", { name }).then(r => r.data);

export const CLIENT_COLORS = {
  "Bairstow Eves": "#1D4ED8",
  "Hamptons": "#7C3AED",
  "Private": "#059669",
  "Gladstone": "#D97706",
  "Keating": "#DC2626",
  "LivelyHood": "#0891B2",
  "HJK Electrical": "#4338CA",
};

export default api;
